// UltraFlex Member Hub - Dashboard with Live Usage & Patterns
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  QrCode, CreditCard, MapPin, Calendar, User, Bell,
  ChevronRight, Clock, CheckCircle, AlertCircle, Users,
  TrendingUp, TrendingDown, Minus, Activity, Dumbbell
} from 'lucide-react';
import QRCode from 'react-qr-code';
import MemberAgreementFlow from '../../components/MemberAgreementFlow';
import WalletButtons from '../../components/WalletButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate mock live usage data
const generateLiveUsage = (baseCapacity = 200) => {
  const hour = new Date().getHours();
  // Simulate realistic gym patterns
  let multiplier = 0.2;
  if (hour >= 6 && hour < 9) multiplier = 0.7; // Morning rush
  else if (hour >= 9 && hour < 12) multiplier = 0.4;
  else if (hour >= 12 && hour < 14) multiplier = 0.5; // Lunch
  else if (hour >= 14 && hour < 17) multiplier = 0.3;
  else if (hour >= 17 && hour < 20) multiplier = 0.85; // Evening rush
  else if (hour >= 20 && hour < 22) multiplier = 0.5;
  
  const variance = (Math.random() - 0.5) * 0.2;
  const current = Math.floor(baseCapacity * (multiplier + variance));
  return Math.max(5, Math.min(current, baseCapacity));
};

// Mock historical pattern data
const generateWeeklyPatterns = () => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
  ];
  
  const patterns = {};
  days.forEach((day, dayIndex) => {
    patterns[day] = timeSlots.map(time => {
      const hour = parseInt(time.split(':')[0]);
      let baseUsage = 20;
      
      // Weekend patterns
      if (dayIndex >= 5) {
        if (hour >= 9 && hour < 12) baseUsage = 55;
        else if (hour >= 12 && hour < 15) baseUsage = 45;
        else if (hour >= 15 && hour < 18) baseUsage = 50;
        else baseUsage = 25;
      } else {
        // Weekday patterns
        if (hour >= 6 && hour < 9) baseUsage = 65; // Morning rush
        else if (hour >= 9 && hour < 12) baseUsage = 35;
        else if (hour >= 12 && hour < 14) baseUsage = 50; // Lunch
        else if (hour >= 14 && hour < 17) baseUsage = 25;
        else if (hour >= 17 && hour < 20) baseUsage = 80; // Evening rush
        else if (hour >= 20 && hour < 22) baseUsage = 45;
      }
      
      const variance = Math.floor((Math.random() - 0.5) * 15);
      return {
        time,
        usage: Math.max(5, Math.min(95, baseUsage + variance))
      };
    });
  });
  return patterns;
};

const MemberHub = () => {
  const { user, token } = useAuth();
  const [memberData, setMemberData] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [agreementRequired, setAgreementRequired] = useState(null); // null=checking, true=needs, false=done
  
  // Live usage state
  const [liveUsage, setLiveUsage] = useState(null);
  const [gymCapacity] = useState(200);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weeklyPatterns, setWeeklyPatterns] = useState({});

  useEffect(() => {
    checkAgreementStatus();
    fetchMemberData();
    fetchNotifications();
    fetchOccupancyData();
    const interval = setInterval(() => { fetchOccupancyData(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkAgreementStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agreements/check/${user.id}`);
      if (res.ok) {
        const data = await res.json();
        setAgreementRequired(!data.has_agreement);
      } else {
        setAgreementRequired(false);
      }
    } catch { setAgreementRequired(false); }
  };

  const fetchOccupancyData = async () => {
    try {
      // Get member's home site first
      const memberRes = await fetch(`${API_URL}/api/members/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const memberData = await memberRes.json();
      const siteId = memberData.home_site_id;
      
      if (siteId) {
        // Fetch live occupancy
        const occupancyRes = await fetch(`${API_URL}/api/occupancy/${siteId}`);
        if (occupancyRes.ok) {
          const occupancyData = await occupancyRes.json();
          setLiveUsage(occupancyData.current_count);
          if (occupancyData.capacity) {
            // Use the capacity from API but don't update state setter
          }
        } else {
          // Fallback to mock if API not available yet
          setLiveUsage(generateLiveUsage(gymCapacity));
        }
        
        // Fetch patterns
        const patternsRes = await fetch(`${API_URL}/api/occupancy/${siteId}/patterns`);
        if (patternsRes.ok) {
          const patternsData = await patternsRes.json();
          // Convert API format to our display format
          if (Object.keys(patternsData).length > 0) {
            const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const formattedPatterns = {};
            dayNames.forEach((day, dayIndex) => {
              const dayData = patternsData[dayIndex] || [];
              formattedPatterns[day] = dayData.map(item => ({
                time: `${String(item.hour).padStart(2, '0')}:00`,
                usage: item.avg_percentage || 0
              }));
            });
            setWeeklyPatterns(formattedPatterns);
          } else {
            // Use generated mock patterns if no real data yet
            setWeeklyPatterns(generateWeeklyPatterns());
          }
        } else {
          setWeeklyPatterns(generateWeeklyPatterns());
        }
      } else {
        // No home site, use mock data
        setLiveUsage(generateLiveUsage(gymCapacity));
        setWeeklyPatterns(generateWeeklyPatterns());
      }
    } catch (error) {
      console.error('Failed to fetch occupancy data:', error);
      // Fallback to mock data
      setLiveUsage(generateLiveUsage(gymCapacity));
      setWeeklyPatterns(generateWeeklyPatterns());
    }
  };

  const fetchMemberData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/members/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMemberData(data);
      
      // Fetch site name if we have home_site_id
      if (data.home_site_id) {
        try {
          const siteRes = await fetch(`${API_URL}/api/sites/${data.home_site_id}`);
          const siteData = await siteRes.json();
          setSiteName(siteData.name || data.home_site_id);
        } catch {
          setSiteName(data.home_site_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications?unread_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setNotifications(data.items || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // QR code now uses static member ID - no generation needed
  // Member number (e.g., UF-000002) is displayed directly for offline use

  const getStatusInfo = () => {
    const status = memberData?.subscription_status;
    const statusConfig = {
      active: { color: 'green', label: 'Active', icon: CheckCircle },
      paid: { color: 'green', label: 'Paid', icon: CheckCircle },
      grace: { color: 'orange', label: 'Grace Period', icon: AlertCircle },
      suspended: { color: 'red', label: 'Suspended', icon: AlertCircle },
      pending: { color: 'yellow', label: 'Pending', icon: Clock }
    };
    return statusConfig[status] || { color: 'gray', label: status || 'Unknown', icon: AlertCircle };
  };

  const getBusynessLevel = (usage) => {
    if (usage < 30) return { label: 'Quiet', color: 'green', icon: TrendingDown };
    if (usage < 60) return { label: 'Moderate', color: 'yellow', icon: Minus };
    return { label: 'Busy', color: 'red', icon: TrendingUp };
  };

  const getCurrentTimeSlotPattern = useCallback(() => {
    const now = new Date();
    const currentDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][now.getDay()];
    const currentHour = now.getHours();
    const patterns = weeklyPatterns[currentDay] || [];
    return patterns.find(p => parseInt(p.time.split(':')[0]) === currentHour);
  }, [weeklyPatterns]);

  if (loading) {
    return (
      <div className="min-h-screen bg-uf-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-uf-red"></div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const usagePercentage = liveUsage ? Math.round((liveUsage / gymCapacity) * 100) : 0;
  const busyness = getBusynessLevel(usagePercentage);
  const BusynessIcon = busyness.icon;
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-uf-black pb-20" data-testid="member-hub">
      {/* Migration Gate: Agreement Required */}
      {agreementRequired === true && (
        <div className="min-h-screen bg-uf-black px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-uf-red rounded flex items-center justify-center font-bold text-white text-lg mx-auto mb-4">UF</div>
              <h1 className="text-2xl font-bold text-white mb-2">Complete Your Membership Agreement</h1>
              <p className="text-zinc-400 text-sm max-w-md mx-auto">We've updated our terms. Please review and confirm the following to continue accessing your UltraFlex membership.</p>
            </div>
            <MemberAgreementFlow
              memberId={user.id}
              memberName={`${user.first_name} ${user.last_name}`}
              onComplete={() => setAgreementRequired(false)}
              isMigration={true}
            />
          </div>
        </div>
      )}

      {/* Normal Hub Content */}
      {agreementRequired !== true && (
        <>
      {/* Header */}
      <div className="bg-gradient-to-b from-uf-red/20 to-uf-black px-4 pt-8 pb-16">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-uf-red rounded flex items-center justify-center font-bold text-white text-sm">UF</div>
              <span className="ml-2 font-heading text-white font-bold">ULTRAFLEX</span>
            </div>
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)} 
                className="relative"
                data-testid="notifications-bell"
              >
                <Bell className="w-6 h-6 text-uf-text" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-uf-red text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-uf-surface border border-uf-border rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notifications-dropdown">
                  <div className="p-3 border-b border-uf-border flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-uf-text">Notifications</h3>
                    {notifications.length > 0 && (
                      <button 
                        onClick={async () => {
                          try {
                            await fetch(`${API_URL}/api/notifications/mark-all-read`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            setNotifications([]);
                          } catch {}
                        }}
                        className="text-xs text-uf-red hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-uf-text-muted text-sm">No new notifications</div>
                    ) : (
                      notifications.map((n, i) => (
                        <div key={i} className="p-3 border-b border-uf-border/50 hover:bg-uf-surface-hi text-sm">
                          <p className="text-uf-text">{n.message || n.title}</p>
                          <p className="text-uf-text-muted text-xs mt-1">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full bg-uf-surface flex items-center justify-center border-2 border-uf-red">
              <span className="text-uf-red text-xl font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="ml-4">
              <h1 className="text-xl font-bold text-uf-text">
                {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-uf-text-muted text-sm font-mono">{memberData?.member_number}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 -mt-8">
        <div className="max-w-lg mx-auto space-y-4">
          
          {/* Live Gym Usage Card */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="live-usage-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-uf-text flex items-center">
                <Activity className="w-5 h-5 mr-2 text-uf-red" />
                Live Gym Status
              </h2>
              <span className="flex items-center text-xs text-uf-text-muted">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></span>
                Live
              </span>
            </div>

            <div className="text-center mb-6">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#2a2a2a"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={usagePercentage < 30 ? '#22c55e' : usagePercentage < 60 ? '#eab308' : '#ef4444'}
                    strokeWidth="12"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${(usagePercentage / 100) * 352} 352`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-uf-text">{liveUsage}</span>
                  <span className="text-xs text-uf-text-muted">people</span>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-center gap-2">
                <BusynessIcon className={`w-5 h-5 text-${busyness.color}-500`} />
                <span className={`font-semibold text-${busyness.color}-500`}>{busyness.label}</span>
                <span className="text-uf-text-muted text-sm">right now</span>
              </div>
              
              <p className="text-uf-text-muted text-sm mt-2">
                {siteName || memberData?.home_site_id || 'Your Gym'} • Capacity: {gymCapacity}
              </p>
            </div>

            {/* Quick Insight */}
            {getCurrentTimeSlotPattern() && (
              <div className="bg-uf-black rounded-lg p-3 text-center">
                <p className="text-sm text-uf-text-muted">
                  Usually <span className={`font-semibold text-${getBusynessLevel(getCurrentTimeSlotPattern().usage).color}-500`}>
                    {getBusynessLevel(getCurrentTimeSlotPattern().usage).label.toLowerCase()}
                  </span> at this time ({getCurrentTimeSlotPattern().usage}% avg)
                </p>
              </div>
            )}
          </div>

          {/* Weekly Usage Patterns */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="weekly-patterns">
            <h2 className="text-lg font-semibold text-uf-text mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-uf-red" />
              When to Visit
            </h2>
            
            {/* Day Selector */}
            <div className="flex justify-between mb-4">
              {days.map((day, i) => {
                const isToday = new Date().getDay() === (i === 6 ? 0 : i + 1);
                const isSelected = selectedDay === fullDays[i];
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(isSelected ? null : fullDays[i])}
                    className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                      isSelected 
                        ? 'bg-uf-red text-white' 
                        : isToday 
                          ? 'bg-uf-red/20 text-uf-red border border-uf-red/50' 
                          : 'bg-uf-black text-uf-text-muted hover:bg-uf-surface-hi'
                    }`}
                    data-testid={`day-${day.toLowerCase()}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Time Slots */}
            {selectedDay && weeklyPatterns[selectedDay] && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-sm text-uf-text-muted mb-3">{selectedDay} - Average busyness by hour</p>
                {weeklyPatterns[selectedDay].map((slot) => {
                  const level = getBusynessLevel(slot.usage);
                  return (
                    <div 
                      key={slot.time} 
                      className="flex items-center gap-3 py-2 border-b border-uf-border last:border-0"
                    >
                      <span className="text-uf-text-muted w-14 text-sm">{slot.time}</span>
                      <div className="flex-1 h-2 bg-uf-black rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full bg-${level.color}-500 transition-all`}
                          style={{ width: `${slot.usage}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium w-16 text-right text-${level.color}-500`}>
                        {level.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {!selectedDay && (
              <p className="text-center text-uf-text-muted text-sm py-4">
                Tap a day above to see hourly patterns
              </p>
            )}
          </div>

          {/* QR Check-in Card - Static Member ID */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
            <h2 className="text-lg font-semibold text-uf-text mb-4 flex items-center">
              <QrCode className="w-5 h-5 mr-2 text-uf-red" />
              Gym Check-in
            </h2>

            {showQR && memberData?.member_number ? (
              <div className="text-center">
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <QRCode value={memberData.member_number} size={180} />
                </div>
                <p className="text-uf-text font-mono text-lg font-semibold mb-2">
                  {memberData.member_number}
                </p>
                <p className="text-uf-text-muted text-sm">
                  Scan this code at the entrance
                </p>
                <p className="text-green-400 text-xs mt-2">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  Static code - works offline & can be saved to wallet
                </p>
                <button
                  onClick={() => setShowQR(false)}
                  className="mt-4 text-uf-text-muted text-sm underline hover:text-uf-text"
                >
                  Hide QR Code
                </button>
              </div>
            ) : (
              <div className="text-center">
                {memberData?.member_number && (
                  <p className="text-uf-text-muted text-sm mb-4">
                    Your Member ID: <span className="font-mono font-semibold text-uf-text">{memberData.member_number}</span>
                  </p>
                )}
                <button
                  onClick={() => setShowQR(true)}
                  disabled={memberData?.subscription_status === 'suspended' || !memberData?.member_number}
                  className="w-full py-4 bg-uf-red text-white font-semibold rounded-lg hover:bg-uf-red-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="show-qr-btn"
                >
                  Show QR Code
                </button>
              </div>
            )}

            {memberData?.subscription_status === 'suspended' && (
              <Link to="/member/arrears" data-testid="resolve-arrears-link"
                className="block mt-3 p-3 bg-red-900/20 border border-red-800 rounded-lg hover:bg-red-900/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-400 text-sm font-medium">Your membership is suspended</p>
                    <p className="text-red-300/70 text-xs mt-0.5">Tap here to settle arrears and reinstate access</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-red-400" />
                </div>
              </Link>
            )}
          </div>

          {/* Digital Wallet Pass */}
          {memberData?.id && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="member-wallet-section">
              <WalletButtons passType="member" entityId={memberData.id} showPreview={true} />
            </div>
          )}

          {/* Membership Status Card */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
            <h2 className="text-lg font-semibold text-uf-text mb-4">Membership</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Status</span>
                <span className={`flex items-center text-${statusInfo.color}-400`}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusInfo.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Plan</span>
                <span className="text-uf-text font-medium">{memberData?.plan_name || 'Standard'}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Home Gym</span>
                <span className="text-uf-text">{siteName || memberData?.home_site_id || 'Not set'}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Monthly Fee</span>
                <span className="text-uf-text font-medium">£{memberData?.monthly_amount || '35.00'}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-3">
            <Link 
              to="/hub/fitness"
              className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center justify-between hover:border-uf-red/50 transition-colors"
              data-testid="quick-link-fitness"
            >
              <div className="flex items-center">
                <Dumbbell className="w-5 h-5 text-uf-red mr-3" />
                <span className="text-uf-text text-sm">Workouts</span>
              </div>
              <ChevronRight className="w-4 h-4 text-uf-text-muted" />
            </Link>

            <Link 
              to="/hub/profile"
              className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center justify-between hover:border-uf-red/50 transition-colors"
            >
              <div className="flex items-center">
                <User className="w-5 h-5 text-uf-red mr-3" />
                <span className="text-uf-text text-sm">Profile</span>
              </div>
              <ChevronRight className="w-4 h-4 text-uf-text-muted" />
            </Link>
            
            <Link 
              to="/hub/profile"
              className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center justify-between hover:border-uf-red/50 transition-colors"
            >
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 text-uf-red mr-3" />
                <span className="text-uf-text text-sm">Payments</span>
              </div>
              <ChevronRight className="w-4 h-4 text-uf-text-muted" />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-uf-surface border-t border-uf-border">
        <div className="max-w-lg mx-auto flex justify-around py-3">
          <Link to="/hub" className="flex flex-col items-center text-uf-red">
            <Activity className="w-6 h-6" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/hub/fitness" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <Dumbbell className="w-6 h-6" />
            <span className="text-xs mt-1">Workouts</span>
          </Link>
          <Link to="/hub/profile" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <User className="w-6 h-6" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
          <Link to="/locations" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <MapPin className="w-6 h-6" />
            <span className="text-xs mt-1">Hub</span>
          </Link>
        </div>
      </nav>
        </>
      )}
    </div>
  );
};

export default MemberHub;
